/**
 * AuthTokensApi — authentication + API-token management.
 *
 * Extracted from client.ts (CUR-547 god-object split, batch 2). Methods-only
 * by design: prototype methods are merged onto AdminAPIClient via applyMixins
 * in client.ts, so the flat `apiClient.method()` surface is unchanged.
 * Instance state lives in BaseApiClient — do not add fields here.
 */

import type { LoginRequest, LoginResponse } from '../../../../shared/types/api';
import { APIError as SharedAPIError, buildApiUrl, createAuthHeaders, processApiResponse } from '../../../../shared/utils/api-helpers';
import type { CreateTokenRequest, CreateTokenResponse, RevokeTokenResponse, TokenListResponse } from '@/types/tokens';
import { API_ENDPOINTS } from '@/utils/env';

import { BaseApiClient } from './base';

// Backend response types (snake_case from FastAPI)
interface BackendAuthUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  avatar_url?: string;
  active?: boolean;
  email_verified?: boolean;
  created_at?: string;
  last_login_at?: string;
}

interface BackendDomainPermission {
  domain: string;
  role: string;
  permissions: string[];
}

interface BackendLoginResponse {
  token: string;
  user: BackendAuthUser;
  domain_permissions: BackendDomainPermission[];
  session_id: string;
  expires_at: string;
}

export class AuthTokensApi extends BaseApiClient {
  // ============================================================================
  // Authentication Methods
  // ============================================================================

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.login);

    // Transform frontend camelCase to backend snake_case
    const backendCredentials = {
      email: credentials.emailAddress,  // Transform emailAddress -> email
      password: credentials.password,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(backendCredentials),
    });

    const result = await processApiResponse<BackendLoginResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    if (!result.data) {
      throw new SharedAPIError(result.status, 'Login failed - no data returned');
    }

    // Transform backend response to frontend format
    return this.transformLoginResponse(result.data);
  }

  private transformLoginResponse(backendResponse: BackendLoginResponse): LoginResponse {
    // Backend returns: { token, user: AuthUser, domain_permissions, session_id, expires_at }
    // Frontend expects: { token, user: User, domainPermissions, sessionId, expiresAt }

    const backendUser = backendResponse.user;

    // Determine user role from domain_permissions
    let role = 'user'; // default
    const domainPermissions = backendResponse.domain_permissions || [];

    // Check if user has super_admin role in any domain
    if (domainPermissions.some((dp: BackendDomainPermission) => dp.role === 'super_admin')) {
      role = 'super_admin';
    } else if (domainPermissions.some((dp: BackendDomainPermission) => dp.role === 'admin')) {
      role = 'admin';
    }

    return {
      token: backendResponse.token,
      user: {
        id: backendUser.id,
        emailAddress: backendUser.email,
        firstName: backendUser.first_name,
        lastName: backendUser.last_name,
        displayName: backendUser.display_name,
        imageUrl: backendUser.avatar_url,
        username: backendUser.email?.split('@')[0], // Generate username from email
        active: backendUser.active,
        emailVerified: backendUser.email_verified,
        createdAt: backendUser.created_at,
        lastLoginAt: backendUser.last_login_at,
        domainPermissions,
        role,
      },
      domainPermissions,
      sessionId: backendResponse.session_id,
      expiresAt: backendResponse.expires_at,
    };
  }

  async logout(): Promise<void> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.logout);
    const token = await this.resolveToken();
    const headers = createAuthHeaders(token);

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const result = await processApiResponse(response);
      throw new SharedAPIError(result.status, result.error?.message || 'Logout failed');
    }
  }

  async refreshToken(): Promise<LoginResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.refresh);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    const result = await processApiResponse<BackendLoginResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    if (!result.data) {
      throw new SharedAPIError(result.status, 'Token refresh failed - no data returned');
    }

    return this.transformLoginResponse(result.data);
  }

  // ============================================================================
  // API Token Methods
  // ============================================================================

  async listTokens(): Promise<TokenListResponse> {
    const url = buildApiUrl(this.baseUrl, '/api/tokens');
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<TokenListResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async createToken(request: CreateTokenRequest): Promise<CreateTokenResponse> {
    const url = buildApiUrl(this.baseUrl, '/api/tokens');
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const result = await processApiResponse<CreateTokenResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async revokeToken(tokenId: string): Promise<RevokeTokenResponse> {
    const url = buildApiUrl(this.baseUrl, `/api/tokens/${tokenId}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    const result = await processApiResponse<RevokeTokenResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

}
